import {
  IConsquenceRecord,
  IIssueBoardApi,
  IIssueBoardBase,
  IIssueBoardDBSchema,
  IMatrixConfig,
  IMatrixItem,
  IRisikoProbability,
} from "@schemas/issueBoardSchema";
import { ICountersForAllAttributesWithFormula, IIssueApi } from "@schemas/issueSchema";
import {
  ICapitalLetter,
  IOtherAttributeConfigApi,
  IOtherAttributeConfigDb,
  IOtherAttributesFormula,
} from "@schemas/otherAttributesSchema";
import * as R from "ramda";

type StringKeyStringValueObject = {
  [key: string]: string;
};

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const endOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
const addDays = (date: Date, days: number): Date =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + days,
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  );
const endOfWeek = (date: Date): Date => endOfDay(addDays(startOfDay(date), 6 - date.getDay()));
const startOfIsoWeek = (date: Date): Date => {
  const day = date.getDay() || 7;
  return startOfDay(addDays(date, 1 - day));
};

export interface IAllOtherAttributeFormulaWithGeneratedStrings {
  [issueId: string]: StringKeyStringValueObject;
}

//Example: Replace "Total -> TOT, Dreike -> DR" in string "Blabla Total Dreike" -> "Blabla TOT DR". valuesToReplace={TOTAL: "TOT", DREIKE: "DR"}
export const replaceMultipleValuesInString = (value: string, valuesToReplace: Record<string, string>): string => {
  if (value == null) {
    return "";
  }

  if (value.includes("Pr")) {
    value = value.replace(/Pr[^ ]*$/, "Pr");
  }

  // Loopa genom varje nyckel i valuesToReplace-objektet och ersätt varje förekomst
  Object.keys(valuesToReplace).forEach((searchValue) => {
    const replaceValue = valuesToReplace[searchValue];
    value = value.replace(new RegExp(searchValue, "g"), replaceValue);
  });

  return value;
};

export const getUniqValues = <T>(arr: T[], key: string): T[] => {
  if (arr == null) {
    return [];
  }

  return arr.reduce((acc: T[], item: T) => {
    if (acc.find((i) => R.path(key.split("."), i) === R.path(key.split("."), item)) == null) {
      acc.push(item);
    }
    return acc;
  }, []);
};

export const getDeeplyNestedValue = (obj: any, path: string): string | undefined => {
  if (obj == null || typeof path !== "string") {
    return undefined;
  }
  const keys = path.split(".");
  if (keys.length === 1) {
    return obj[path];
  }
  const result = keys.reduce((acc, key) => {
    if (acc == null || typeof acc !== "object") {
      return undefined;
    }
    return acc[key];
  }, obj);
  return typeof result === "string" ? result : undefined;
};

export const getStringFromNrOfCharsInFormula = (nrOfChars: number, value: number): string => {
  //nrOfChars = 3, value = 1 => "001", nrOfChars = 3, value = 10 => "010", nrOfChars = 3, value = 100 => "100", nrOfChars = 3, value = 1000 => "1000"
  const valueAsString = value.toString();
  const length = valueAsString.length;
  if (length >= nrOfChars) {
    return valueAsString;
  }
  const diff = nrOfChars - length;
  return "0".repeat(diff) + valueAsString;
};

interface IArgsFormatIssueFormulaGeneratedString {
  formula: IOtherAttributesFormula;
  countForIssue: number | null;
  deletePreviousDelimiterIfAttributeIsEmpty: boolean;
  issue: Partial<IIssueApi>;
  otherAttributesConfig: IOtherAttributeConfigDb | IOtherAttributeConfigApi;
}

export const formatIssueFormulaGeneratedString = (args: IArgsFormatIssueFormulaGeneratedString): string => {
  let generatedString = "";
  const config = args.otherAttributesConfig || {};

  return args.formula.confArr.reduce((acc, conf) => {
    if (conf.type === "DELIMITER") {
      acc += conf.value;
    }
    if (conf.type === "ATTRIBUTE") {
      const value = args.issue.otherAttributesData?.[conf.value] ?? args.issue[conf.value];

      if (value != null && value !== "") {
        if (config[conf.value]?.typeOfValue === "DATE") {
          acc += formatShortDate(value);
        } else {
          acc += value;
        }
      } else if (args.deletePreviousDelimiterIfAttributeIsEmpty) {
        // Remove the last character if it's a delimiter
        const previousConf = args.formula.confArr[args.formula.confArr.indexOf(conf) - 1];
        if (acc.length > 0 && previousConf?.type === "DELIMITER") {
          const nrOfCharsInDelimiter = previousConf.value.length;
          acc = acc.slice(0, -nrOfCharsInDelimiter);
        }
      }
    }
    if (conf.type === "COUNTER") {
      if (args.countForIssue == null) {
        const previousConf = args.formula.confArr[args.formula.confArr.indexOf(conf) - 1];

        if (acc.length > 0 && previousConf?.type === "DELIMITER") {
          acc = acc.slice(0, -previousConf.value.length);
        }

        return acc;
      }

      acc += getStringFromNrOfCharsInFormula(args.formula.nrOfCharsInCounter, args.countForIssue);
    }
    return acc;
  }, generatedString);
};

type IssueCountEntry = {
  issueCounts: {
    [key: string]: number;
  };
};

function mergeIssueCounts(data: IssueCountEntry[]): { [key: string]: number } {
  return data.reduce((acc, entry) => {
    Object.entries(entry.issueCounts).forEach(([key, value]) => {
      acc[key] = value;
    });
    return acc;
  }, {} as { [key: string]: number });
}

interface IArgGeneratedStringsForFormula {
  issueBoard: IIssueBoardBase | IIssueBoardApi | IIssueBoardDBSchema;
  countersForIssuesOnAllKeys: ICountersForAllAttributesWithFormula;
  issues: IIssueApi[];
}

export const getGeneratedStringForFormulaOnAllKeys = (
  args: IArgGeneratedStringsForFormula,
): IAllOtherAttributeFormulaWithGeneratedStrings => {
  const generatedStrings: IAllOtherAttributeFormulaWithGeneratedStrings = {};

  if (args.issues == null) {
    return generatedStrings;
  }

  args.issues.forEach((issue) => {
    if (issue == null) return;
    if (issue.taskOrIssue === "TASK") return;

    const issueId = issue.id;
    const otherAttributesData = issue.otherAttributesData;

    Object.keys(args.issueBoard.otherAttributesConfig || {}).forEach((key) => {
      const attribute = args.issueBoard.otherAttributesConfig[key];
      if (attribute.typeOfValue === "FORMULA") {
        const formula = attribute.formula;
        const deletePreviousDelimiterIfAttributeIsEmpty =
          attribute?.formula?.deletePreviousDelimiterIfAttributeIsEmpty ?? false;
        const issueCounts = mergeIssueCounts(args.countersForIssuesOnAllKeys[key] || []);
        const manualCounterValue = formula.counterMode === "MANUAL" ? issue.formulaCounters?.[key]?.value : null;
        const generatedString = formatIssueFormulaGeneratedString({
          formula,
          countForIssue: formula.counterMode === "MANUAL" ? manualCounterValue ?? null : issueCounts[issueId] ?? 0,
          deletePreviousDelimiterIfAttributeIsEmpty,
          issue: { ...issue, otherAttributesData },
          otherAttributesConfig: args.issueBoard.otherAttributesConfig,
        });
        generatedStrings[issueId] = generatedStrings[issueId] || {};
        generatedStrings[issueId][key] = generatedString;
      }
    });
  });
  return generatedStrings;
};

export const issueBoardContainsFormula = (
  issueBoard: IIssueBoardBase | IIssueBoardApi | IIssueBoardDBSchema,
): boolean => {
  return (
    Object.values(issueBoard?.otherAttributesConfig ?? {}).find((attribute) => attribute.typeOfValue === "FORMULA") !=
    null
  );
};

interface IArgGetIssuesWithFormulaAdded {
  issues: IIssueApi[];
  issueBoard: IIssueBoardBase | IIssueBoardApi | IIssueBoardDBSchema;
  sumsByFormula: ICountersForAllAttributesWithFormula;
}
export const getIssuesWithFormulaAdded = (args: IArgGetIssuesWithFormulaAdded): IIssueApi[] => {
  if (args.issues == null || args.issueBoard == null || args.sumsByFormula == null) {
    return args.issues;
  }
  try {
    const issuesFormulas = getGeneratedStringForFormulaOnAllKeys({
      issueBoard: args.issueBoard,
      countersForIssuesOnAllKeys: args.sumsByFormula,
      issues: args.issues,
    });

    return args.issues.map((issue) => {
      const issueId = issue.id;
      if (issuesFormulas[issueId] != null) {
        return {
          ...issue,
          otherAttributesData: {
            ...issue.otherAttributesData,
            ...issuesFormulas[issueId],
          },
        };
      }
      return { ...issue };
    });
  } catch (error) {
    console.error("Error in getIssuesWithFormulaAdded:", error);
    return args.issues;
  }
};

export const formatShortDate = (dateStr, hideOldDate = false) => {
  try {
    if (dateStr == null) {
      return "";
    }

    const date = new Date(dateStr);

    if (isNaN(date.getTime())) {
      return dateStr;
    }

    if (hideOldDate === true) {
      if (date < new Date("2000-01-01")) {
        return "";
      }
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${day}.${month}.${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateStr;
  }
};

interface IGetMatrixArgs {
  issue: IIssueApi;
  issueBoard: IIssueBoardBase | IIssueBoardApi | IIssueBoardDBSchema;
  evaluationCategoryCapital: ICapitalLetter;
}

export interface IMatrixOutput {
  matrixItem: IMatrixItem | undefined;
  score: number;
}

export const getMatrixItemAndScore = (args: IGetMatrixArgs): IMatrixOutput => {
  let score;
  try {
    if (
      args.issue.risiko?.probability == null ||
      args.issue.risiko?.consequence == null ||
      args.issue.risiko?.consequence[args.evaluationCategoryCapital] == null
    ) {
      return {
        matrixItem: undefined,
        score: 0,
      };
    }
    const probability: IRisikoProbability = args.issueBoard.risikoSection.probabilites[args.issue.risiko.probability];
    const probabilityOrder = probability.order;
    const probabilityMatrixValue = probability.matrixValue;
    const consequences: IConsquenceRecord =
      args.issueBoard.risikoSection.evaluationCategoriesConfig[args.evaluationCategoryCapital].consequences;
    try {
      const consequence = consequences[args.issue.risiko.consequence[args.evaluationCategoryCapital]];
      const consequenceOrder = consequence.order;
      const consequenceMatrixValue = consequence.matrixValue;

      score = probabilityMatrixValue * consequenceMatrixValue;
      const matrix: IMatrixConfig =
        args.issueBoard.risikoSection.evaluationCategoriesConfig[args.evaluationCategoryCapital].matrixConfig;
      return {
        matrixItem: matrix[consequenceOrder][probabilityOrder],
        score,
      };
    } catch (error) {
      return {
        matrixItem: undefined,
        score,
      };
    }
  } catch (error) {
    console.error("failed to get matrix item", error);
    return undefined;
  }
};

export type TidsfristBucket = "Ingen dato" | "Utløpt" | "I dag" | "I morgen" | "Denne uken" | "Neste uke" | "Fremtidig";

// Hjälpare: välj ett konkret due-datum som landar i rätt tidsfrist-bucket
export function computeDueForTidsfristBucket(targetColumn: string, current?: Date | string | null): Date | null {
  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);
  const thisWeekEnd = endOfWeek(today);
  const nextWeekEnd = endOfWeek(addDays(today, 7));

  switch (targetColumn) {
    case "Ingen dato":
      return null;
    case "Utløpt":
      // en dag bakåt → försenad
      return endOfDay(addDays(today, -1));
    case "I dag":
      return endOfDay(today);
    case "I morgen":
      return endOfDay(tomorrow);
    case "Denne uken":
      return endOfDay(thisWeekEnd);
    case "Neste uke":
      return endOfDay(nextWeekEnd);
    case "Fremtidig":
      // första dagen efter nästa vecka (mån) som enkel standard
      return endOfDay(addDays(nextWeekEnd, 1));
    default:
      return current ? new Date(current) : null;
  }
}

// Hjälpare: klassificera tidsfrist-kolumn utifrån dates.due
export function classifyTidsfrist(due?: string | Date | null): string {
  if (due == null || due === "") return "Ingen dato";
  const dueDate = startOfDay(new Date(due));
  if (Number.isNaN(dueDate.getTime())) return "Ingen dato";

  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);
  const thisWeekEnd = endOfWeek(today);
  const nextWeekStart = startOfIsoWeek(addDays(today, 7));
  const nextWeekEnd = endOfWeek(addDays(today, 7));

  if (dueDate < today) return "Utløpt";
  if (dueDate.getTime() === today.getTime()) return "I dag";
  if (dueDate.getTime() === tomorrow.getTime()) return "I morgen";
  if (dueDate > tomorrow && dueDate <= thisWeekEnd) return "Denne uken";
  if (dueDate >= nextWeekStart && dueDate <= nextWeekEnd) return "Neste uke";
  return "Fremtidig";
}
